<template>
  <div class="modal-overlay" @click="closeModal" v-if="isVisible && !isAddCardModalOpen && !isBillingModalOpen">
    <div class="modal-container" @click.stop>
      <div class="modal-header">
        <button class="back-btn" @click="closeModal">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M19 12H5M12 19L5 12L12 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
        <div class="modal-title">
          <div class="payment-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <rect x="2" y="6" width="20" height="12" rx="2" fill="#3B82F6"/>
              <rect x="2" y="8" width="20" height="2" fill="white"/>
            </svg>
          </div>
          <span>{{$t('payment_information')}}</span>
        </div>
        <button class="close-btn" @click="closeModal">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>

      <div class="modal-content">
        <div class="content-wrapper">
          <div class="payment-section">

            <div class="section">
              <h4 class="section-title">{{ $t('payment_method') }}</h4>

              <div v-if="loading" class="cards-loading">
                {{ $t('loading') }}...
              </div>

              <div v-else class="card-select-row">
                <div class="card-select-wrapper" v-click-outside="closeCardDropdown">
                  <div v-if="paymentMethods.length > 0" class="card-select-trigger" @click="cardDropdownOpen = !cardDropdownOpen">
                    <div class="card-select-display">
                      <img v-if="selectedCard" :src="'/card/'+selectedCard.cardBrand+'.svg'" alt="" class="card-select-icon" />
                      <span v-if="selectedCard" class="card-select-text">
                        {{ getCardBrandName(selectedCard.cardBrand) }} {{ selectedCard.maskedCardNumber }}
                        <span class="card-select-expiry">{{ String(selectedCard.expiryMonth).padStart(2, '0') }}/{{ selectedCard.expiryYear }}</span>
                      </span>
                      <span v-else class="card-select-placeholder">{{ $t('select_card') }}</span>
                    </div>
                    <img src="@/assets/images/arrowdown.svg" class="card-select-arrow" :class="{ 'rotated': cardDropdownOpen }" />
                  </div>

                  <div v-else class="no-card-info">
                    <span>{{ $t('no_cards_registered_yet') }}</span>
                  </div>

                  <div v-if="cardDropdownOpen" class="card-select-dropdown">
                    <div
                        v-for="card in paymentMethods"
                        :key="card.cardId"
                        class="card-select-option"
                        :class="{ 'selected': selectedCardId === card.cardId }"
                        @click="selectPaymentCard(card.cardId)"
                    >
                      <img :src="'/card/'+card.cardBrand+'.svg'" alt="" class="card-select-icon" />
                      <div class="card-option-details">
                        <span class="card-option-brand">{{ getCardBrandName(card.cardBrand) }}</span>
                        <span class="card-option-number">{{ card.maskedCardNumber }}</span>
                      </div>
                      <span class="card-option-expiry">{{ String(card.expiryMonth).padStart(2, '0') }}/{{ card.expiryYear }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="section">
              <h4 class="section-title">{{$t('bill_information')}}</h4>
              <div v-if="billingLoading" class="billing-loading">
                {{ $t('loading') }}...
              </div>
              <div v-else-if="billingInfo?.name" class="billing-details">
                <div class="form-row">
                  <p class="billing-name">{{ billingInfo.name }}</p>
                </div>
                <div class="address-info">
                  <p>{{ billingInfo.email }}</p>
                  <p>{{ billingInfo.phone }}</p>
                  <p>{{ billingInfo.address }}</p>
                </div>
              </div>
              <div v-else class="no-billing">
                <p>{{ $t('no_billing_info') }}</p>
                <a href="javascript:void(0)" class="add-billing-link" @click="openBillingModal">
                  {{ $t('add_update') }}
                </a>
              </div>
            </div>


            <div class="payment-btn-container desktop-only" @click="handlePaymentButtonClick">
              <button
                  class="payment-btn"
                  :disabled="isProcessing || !allAgreementsAccepted || !selectedCardId || !billingInfo?.billingId"
              >
                {{ isProcessing ? this.$t('processing')+'...' : this.$t('complete_payment') }}
              </button>
            </div>

            <div class="primary-button-wrapper">
              <button class="add-new-btn" @click="openAddCardModal">
                {{ $t('add_new_card') }}
              </button>
            </div>
          </div>

          <div class="summary-wrapper">
            <div class="summary-section">
              <div class="product-section">
                <div class="product-header">
                  <img :src="product.image || defaultImage" :alt="product.title" class="product-image" />
                  <div class="product-info">
                    <h3 class="product-title">{{ product.title }}</h3>
                  </div>
                </div>
              </div>

              <div class="order-summary">
                <h4 class="section-title">{{$t('transaction_summary')}}</h4>

                <div v-if="product.exchangeRate" class="exchange-rate-info">
                  <div class="price-row">
                    <span class="price-label">{{$t('original_price')}}</span>
                    <span class="price-value">{{ product.exchangeRate.originalPrice }} {{ product.exchangeRate.originalCurrency }}</span>
                  </div>
                  <div class="price-row">
                    <span class="price-label">{{$t('exchange_rate')}}</span>
                    <span class="price-value">{{ parseFloat(product.exchangeRate.sellRate).toFixed(2) }}</span>
                  </div>
                </div>
                <div class="price-row">
                  <span class="price-label">{{$t('product_price')}}</span>
                  <span class="price-value">{{ product.formattedPrice}}</span>
                </div>

                <div class="price-row discount-row" v-if="discountAmount > 0">
                  <span class="price-label">
                    {{ $t('discount') }}
                    <span v-if="appliedPromotion" class="discount-code">({{ appliedPromotion.code }} - %{{ discountPercentage }})</span>
                  </span>
                  <span class="price-value discount">-{{ formatPrice(discountAmount) }}</span>
                </div>

                <div class="divider"></div>

                <div class="price-row total-row">
                  <span class="price-label">{{$t('total_amount')}}</span>
                  <span class="price-value total">{{ product.formattedPrice }}</span>
                </div>

                <div class="promo-section">
                  <div class="promo-input-group" v-if="!appliedPromotion">
                    <input
                        type="text"
                        placeholder="ibility10"
                        class="promo-input"
                        v-model="promoCode"
                        @keyup.enter="applyPromoCode"
                    />
                    <button
                        class="promo-btn"
                        @click="applyPromoCode"
                        :disabled="isApplyingPromo || !promoCode.trim()"
                    >
                      {{ isApplyingPromo ? this.$t('checking')+'...' : this.$t('apply') }}
                    </button>
                  </div>

                  <div class="applied-promo" v-if="appliedPromotion">
                    <div class="applied-promo-info">
                      <span class="applied-promo-code">{{ appliedPromotion.code }}</span>
                      <span class="applied-promo-discount">%{{ discountPercentage }} {{$t('discount_applied')}}</span>
                    </div>
                    <button class="remove-promo-btn" @click="removePromoCode">
                      {{ $t('remove') }}
                    </button>
                  </div>
                </div>

                <div class="security-badge">
                  <span>{{$t('purchase_safely')}}</span>
                </div>

                <div class="legal-agreements">
                  <div class="agreement-item-wrapper">
                    <div class="agreement-item">
                      <label class="checkbox-label">
                        <input type="checkbox" v-model="agreements.preInformation" />
                        <span class="checkmark"></span>
                      </label>
                      <span class="agreement-text">
                        <a href="javascript:void(0)" class="agreement-link" @click.prevent="openPreInformationModal">
                          {{ $t('pre_information_form') }}
                        </a>
                        {{ $t('i_have_read_and_accept') }}
                      </span>
                    </div>
                    <span v-if="showPreInformationError" class="agreement-error">
                      {{ $t('this_field_is_required') }}
                    </span>
                  </div>

                  <div class="agreement-item-wrapper">
                    <div class="agreement-item">
                      <label class="checkbox-label">
                        <input type="checkbox" v-model="agreements.distanceSales" />
                        <span class="checkmark"></span>
                      </label>
                      <span class="agreement-text">
                        <a href="javascript:void(0)" class="agreement-link" @click.prevent="openDistanceSalesModal">
                          {{ $t('distance_sales_contract') }}
                        </a>
                        {{ $t('i_have_read_and_accept') }}
                      </span>
                    </div>
                    <span v-if="showDistanceSalesError" class="agreement-error">
                      {{ $t('this_field_is_required') }}
                    </span>
                  </div>

                  <div class="agreement-item-wrapper">
                    <div class="agreement-item">
                      <label class="checkbox-label">
                        <input type="checkbox" v-model="agreements.immediateService" />
                        <span class="checkmark"></span>
                      </label>
                      <span class="agreement-text">
                        {{ $t('immediate_service_request') }}
                      </span>
                    </div>
                    <span v-if="showImmediateServiceError" class="agreement-error">
                      {{ $t('this_field_is_required') }}
                    </span>
                  </div>

                  <div class="agreement-item agreement-link-only">
                    <span class="agreement-text">
                      <a href="https://ibilityapp.com/tr/cancellation-refund" target="_blank" class="agreement-link">
                        {{ $t('cancellation_refund_policy') }}
                      </a>
                    </span>
                  </div>

                  <div class="agreement-item agreement-link-only">
                    <span class="agreement-text">
                      <a href="https://ibilityapp.com/tr/privacy-policy" target="_blank" class="agreement-link">
                        {{ $t('privacy_policy') }}
                      </a>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="mobile-payment-btn-wrapper" @click="handlePaymentButtonClick">
          <button
              class="payment-btn"
              :disabled="isProcessing || !allAgreementsAccepted"
          >
            {{ isProcessing ? this.$t('processing')+'...' : this.$t('complete_payment') }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <PricingAddNewCardModal
      :is-visible="isAddCardModalOpen"
      :skip3DSecure="false"
      @close="closeAddCardModal"
      @save="onCardSave"
  />

  <PricingInformation
      :is-visible="isBillingModalOpen"
      :billing-info="rawBillingInfo"
      :mode="rawBillingInfo.billingId ? 'update' : 'add'"
      @close="closeBillingModal"
      @save="onBillingSave"
  />

  <div class="legal-modal-overlay" v-if="isPreInformationModalOpen" @click="closePreInformationModal">
    <div class="legal-modal-container" @click.stop>
      <div class="legal-modal-header">
        <h3>{{ $t('pre_information_form') }}</h3>
        <button class="legal-modal-close" @click="closePreInformationModal">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>
      <div class="legal-modal-content">
        <div class="legal-text">
          <h3 class="legal-main-title">{{ $t('legal_pre_info_title') }}</h3>
          <p class="legal-subtitle">{{ $t('legal_pre_info_subtitle') }}</p>

          <h4>{{ $t('legal_pre_info_section_1_title') }}</h4>
          <p>{{ $t('legal_pre_info_section_1_intro') }}</p>
          <p><strong>{{ $t('legal_company_name_label') }}</strong><br/>{{ $t('legal_company_name') }}</p>
          <p><strong>{{ $t('legal_company_address_label') }}</strong><br/>{{ $t('legal_company_address') }}</p>
          <p>{{ $t('legal_company_address_note') }}</p>
          <p><strong>{{ $t('legal_tax_office_label') }}</strong> {{ $t('legal_tax_office') }}<br/><strong>{{ $t('legal_tax_number_label') }}</strong> {{ $t('legal_tax_number') }}</p>
          <p><strong>{{ $t('legal_phone_label') }}</strong> {{ $t('legal_phone') }}<br/><strong>{{ $t('legal_email_label') }}</strong> {{ $t('legal_email') }}</p>
          <p>{{ $t('legal_email_note') }}</p>

          <h4>{{ $t('legal_pre_info_section_2_title') }}</h4>
          <p>{{ $t('legal_pre_info_section_2_p1') }}</p>
          <p>{{ $t('legal_pre_info_section_2_p2') }}</p>
          <ul>
            <li>{{ $t('legal_pre_info_section_2_item_1') }}</li>
            <li>{{ $t('legal_pre_info_section_2_item_2') }}</li>
            <li>{{ $t('legal_pre_info_section_2_item_3') }}</li>
            <li>{{ $t('legal_pre_info_section_2_item_4') }}</li>
            <li>{{ $t('legal_pre_info_section_2_item_5') }}</li>
          </ul>
          <p>{{ $t('legal_pre_info_section_2_p3') }}</p>
          <p>{{ $t('legal_pre_info_section_2_p4') }}</p>

          <h4>{{ $t('legal_pre_info_section_3_title') }}</h4>
          <p>{{ $t('legal_pre_info_section_3_p1') }}</p>
          <p>{{ $t('legal_pre_info_section_3_p2') }}</p>
          <p>{{ $t('legal_pre_info_section_3_p3') }}</p>
          <p><strong>{{ $t('legal_product_name_label') }}</strong> {{ product.title }}<br/><strong>{{ $t('legal_product_price_label') }}</strong> {{ product.formattedPrice }}</p>

          <h4>{{ $t('legal_pre_info_section_4_title') }}</h4>
          <p>{{ $t('legal_pre_info_section_4_p1') }}</p>
          <p>{{ $t('legal_pre_info_section_4_p2') }}</p>
          <ul>
            <li>{{ $t('legal_pre_info_section_4_item_1') }}</li>
            <li>{{ $t('legal_pre_info_section_4_item_2') }}</li>
            <li>{{ $t('legal_pre_info_section_4_item_3') }}</li>
          </ul>
          <p>{{ $t('legal_pre_info_section_4_p3') }}</p>
          <p>{{ $t('legal_pre_info_section_4_p4') }}</p>

          <h4>{{ $t('legal_pre_info_section_5_title') }}</h4>
          <p>{{ $t('legal_pre_info_section_5_p1') }}</p>
          <p>{{ $t('legal_pre_info_section_5_p2') }}</p>
          <p>{{ $t('legal_pre_info_section_5_p3') }}</p>

          <h4>{{ $t('legal_pre_info_section_6_title') }}</h4>
          <p>{{ $t('legal_pre_info_section_6_p1') }}</p>
          <p>{{ $t('legal_pre_info_section_6_p2') }}</p>

          <h4>{{ $t('legal_pre_info_section_7_title') }}</h4>
          <p>{{ $t('legal_pre_info_section_7_p1') }}</p>
          <p><em>{{ $t('legal_pre_info_section_7_quote') }}</em></p>
          <p>{{ $t('legal_pre_info_section_7_p2') }}</p>
          <ul>
            <li>{{ $t('legal_pre_info_section_7_item_1') }}</li>
            <li>{{ $t('legal_pre_info_section_7_item_2') }}</li>
            <li>{{ $t('legal_pre_info_section_7_item_3') }}</li>
          </ul>
          <p><strong>{{ $t('legal_pre_info_section_7_conclusion') }}</strong></p>

          <h4>{{ $t('legal_pre_info_section_8_title') }}</h4>
          <p>{{ $t('legal_pre_info_section_8_p1') }}</p>
          <ul>
            <li>{{ $t('legal_pre_info_section_8_item_1') }}</li>
            <li>{{ $t('legal_pre_info_section_8_item_2') }}</li>
            <li>{{ $t('legal_pre_info_section_8_item_3') }}</li>
          </ul>
          <p>{{ $t('legal_pre_info_section_8_p2') }}</p>
          <p>{{ $t('legal_pre_info_section_8_p3') }}</p>

          <h4>{{ $t('legal_pre_info_section_9_title') }}</h4>
          <p>{{ $t('legal_pre_info_section_9_p1') }}</p>
          <p>{{ $t('legal_pre_info_section_9_p2') }}</p>

          <h4>{{ $t('legal_pre_info_section_10_title') }}</h4>
          <p>{{ $t('legal_pre_info_section_10_p1') }}</p>
          <p>{{ $t('legal_pre_info_section_10_p2') }}</p>
          <ul>
            <li>{{ $t('legal_pre_info_section_10_item_1') }}</li>
            <li>{{ $t('legal_pre_info_section_10_item_2') }}</li>
          </ul>
          <p>{{ $t('legal_pre_info_section_10_p3') }}</p>

          <h4>{{ $t('legal_pre_info_section_11_title') }}</h4>
          <p>{{ $t('legal_pre_info_section_11_p1') }}</p>
          <p>{{ $t('legal_pre_info_section_11_p2') }}</p>
        </div>
      </div>
      <div class="legal-modal-footer">
        <button class="primary-button" @click="acceptPreInformation">
          {{ $t('i_have_read_and_accept') }}
        </button>
      </div>
    </div>
  </div>

  <div class="legal-modal-overlay" v-if="isDistanceSalesModalOpen" @click="closeDistanceSalesModal">
    <div class="legal-modal-container" @click.stop>
      <div class="legal-modal-header">
        <h3>{{ $t('distance_sales_contract') }}</h3>
        <button class="legal-modal-close" @click="closeDistanceSalesModal">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>
      <div class="legal-modal-content">
        <div class="legal-text">
          <h3 class="legal-main-title">{{ $t('legal_distance_sales_title') }}</h3>

          <h4>{{ $t('legal_distance_sales_section_1_title') }}</h4>
          <p>{{ $t('legal_distance_sales_section_1_p1') }}</p>

          <p><strong>{{ $t('legal_buyer_info_label') }}</strong></p>
          <p>{{ $t('legal_buyer_name_label') }} {{ billingInfo?.name }}</p>
          <p>{{ $t('legal_buyer_email_label') }} {{ billingInfo?.email }}</p>

          <h4>{{ $t('legal_distance_sales_section_2_title') }}</h4>
          <p>{{ $t('legal_distance_sales_section_2_p1') }}</p>

          <p><strong>{{ $t('legal_contract_product_label') }}</strong></p>
          <p>{{ $t('legal_product_name_label') }} {{ product.title }}</p>
          <p>{{ $t('legal_product_price_label') }} {{ product.formattedPrice }}</p>

          <h4>{{ $t('legal_distance_sales_section_3_title') }}</h4>
          <p>{{ $t('legal_distance_sales_section_3_p1') }}</p>
          <p>{{ $t('legal_distance_sales_section_3_p2') }}</p>
          <p>{{ $t('legal_distance_sales_section_3_p3') }}</p>

          <h4>{{ $t('legal_distance_sales_section_4_title') }}</h4>
          <p>{{ $t('legal_distance_sales_section_4_p1') }}</p>
          <p>{{ $t('legal_distance_sales_section_4_p2') }}</p>

          <h4>{{ $t('legal_distance_sales_section_5_title') }}</h4>
          <p>{{ $t('legal_distance_sales_section_5_p1') }}</p>
          <p>{{ $t('legal_distance_sales_section_5_p2') }}</p>
          <p>{{ $t('legal_distance_sales_section_5_p3') }}</p>

          <h4>{{ $t('legal_distance_sales_section_6_title') }}</h4>
          <p>{{ $t('legal_distance_sales_section_6_p1') }}</p>
          <p>{{ $t('legal_distance_sales_section_6_p2') }}</p>
          <p>{{ $t('legal_distance_sales_section_6_p3') }}</p>

          <h4>{{ $t('legal_distance_sales_section_7_title') }}</h4>
          <p>{{ $t('legal_distance_sales_section_7_p1') }}</p>
          <p>{{ $t('legal_distance_sales_section_7_p2') }}</p>

          <h4>{{ $t('legal_distance_sales_section_8_title') }}</h4>
          <p>{{ $t('legal_distance_sales_section_8_p1') }}</p>
          <p>{{ $t('legal_distance_sales_section_8_p2') }}</p>

          <h4>{{ $t('legal_distance_sales_section_9_title') }}</h4>
          <p>{{ $t('legal_distance_sales_section_9_p1') }}</p>
          <p>{{ $t('legal_distance_sales_section_9_p2') }}</p>

          <h4>{{ $t('legal_distance_sales_section_10_title') }}</h4>
          <p>{{ $t('legal_distance_sales_section_10_p1') }}</p>
          <p>{{ $t('legal_distance_sales_section_10_p2') }}</p>

          <h4>{{ $t('legal_distance_sales_section_11_title') }}</h4>
          <p>{{ $t('legal_distance_sales_section_11_p1') }}</p>
          <p>{{ $t('legal_distance_sales_section_11_p2') }}</p>
          <p>{{ $t('legal_distance_sales_section_11_p3') }}</p>
        </div>
      </div>
      <div class="legal-modal-footer">
        <button class="primary-button" @click="acceptDistanceSales">
          {{ $t('i_have_read_and_accept') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import defaultImage from '@/assets/images/ibility-default.jpeg';
import promotionCodeService from '@/service/promotionCodeService';
import marketplaceService from '@/service/marketplaceService';
import pricingService from '@/service/pricingService';
import PricingAddNewCardModal from '@/components/modals/PricingAddNewCardModal.vue';
import PricingInformation from '@/components/modals/PricingInformation.vue';

export default {
  name: 'PurchaseModal',
  components: {
    PricingAddNewCardModal,
    PricingInformation
  },
  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    product: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      defaultImage,
      billingInfo: null,
      billingLoading: false,
      promoCode: '',
      discountAmount: 0,
      discountPercentage: 0,
      appliedPromotion: null,
      isApplyingPromo: false,
      isProcessing: false,
      paymentMethods: [],
      loading: false,
      selectedCardId: null,
      cardDropdownOpen: false,
      isAddCardModalOpen: false,
      isBillingModalOpen: false,
      rawBillingInfo: {},
      agreements: {
        preInformation: false,
        distanceSales: false,
        immediateService: false
      },
      isPreInformationModalOpen: false,
      isDistanceSalesModalOpen: false,
      showValidationErrors: false
    }
  },
  watch: {
    isVisible(newVal) {
      if (newVal) {
        this.getBillingInfo();
        this.getCards();
      } else {
        this.showValidationErrors = false;
        this.agreements = {
          preInformation: false,
          distanceSales: false,
          immediateService: false
        };
      }
    }
  },
  computed: {
    productPrice() {
      if (this.product.price === '0.00' || this.product.price === 0) {
        return 0;
      }
      const priceStr = this.product.price || '0';
      if (typeof priceStr === 'string' && !isNaN(parseFloat(priceStr))) {
        return parseFloat(priceStr);
      }
      const formattedStr = this.product.formattedPrice || priceStr || '0';
      const cleanStr = formattedStr
          .replace(/[^\d,.]/g, '')
          .replace(/\./g, '')
          .replace(',', '.');
      return parseFloat(cleanStr) || 0;
    },
    totalAmount() {
      const total = this.productPrice - this.discountAmount;
      return this.formatPrice(total);
    },
    selectedCard() {
      if (!this.selectedCardId || !this.paymentMethods?.length) return null;
      return this.paymentMethods.find(card => card.cardId === this.selectedCardId);
    },
    allAgreementsAccepted() {
      return this.agreements.preInformation &&
             this.agreements.distanceSales &&
             this.agreements.immediateService;
    },
    showPreInformationError() {
      return this.showValidationErrors && !this.agreements.preInformation;
    },
    showDistanceSalesError() {
      return this.showValidationErrors && !this.agreements.distanceSales;
    },
    showImmediateServiceError() {
      return this.showValidationErrors && !this.agreements.immediateService;
    }
  },
  methods: {
    closeModal() {
      this.$emit('close');
    },
    async processPayment() {
      if (this.isProcessing) return;
      if (!this.selectedCardId) {
        this.$snackbar.add({
          type: 'error',
          text: this.$t('please_select_card')
        });
        return;
      }
      if (!this.billingInfo?.billingId) {
        this.$snackbar.add({
          type: 'error',
          text: this.$t('billing_required_before_payment') || 'Odeme yapabilmek icin once fatura adresi ekleyin.'
        });
        return;
      }
      this.isProcessing = true;
      try {
        const purchaseData = {
          productUuid: this.product.uuid,
          cardId: this.selectedCardId,
          billingId: this.billingInfo.billingId,
        };
        if (this.appliedPromotion && this.appliedPromotion.code) {
          purchaseData.promotionCode = this.appliedPromotion.code;
        }
        const response = await marketplaceService.buyProduct(purchaseData);
        if (response.data.responseType === 'success') {
          this.$snackbar.add({
            type: 'success',
            text: this.$t('payment_successful')
          });
          this.$emit('payment-success', {
            product: this.product,
            amount: this.totalAmount,
            promoCode: this.appliedPromotion?.code || null,
            purchaseData: response.data.data
          });
        } else {
          throw new Error(response.data.message || this.$t('payment_failed'));
        }
      } catch (error) {
        const errorMessage = error.response?.data?.message ||
            error.message || this.$t('error_occurred_payment');
        this.$snackbar.add({
          type: 'error',
          text: errorMessage
        });
      } finally {
        this.isProcessing = false;
      }
    },
    async getBillingInfo() {
      this.billingLoading = true;
      try {
        const response = await pricingService.getBillingInfo();
        if (response?.data?.responseType === 'success') {
          const billingData = response.data?.data?.data || [];
          if (billingData.length > 0) {
            const activeBilling = billingData.find(item => item.isActive && item.isDefault) || billingData[0];
            if (activeBilling) {
              this.billingInfo = {
                billingId: activeBilling.billingId,
                name: `${activeBilling.firstName || ''} ${activeBilling.lastName || ''}`.trim() || activeBilling.companyName || '',
                email: activeBilling.email || '',
                phone: activeBilling.phone || '',
                address: activeBilling.addressLine || ''
              };
            } else {
              this.billingInfo = null;
            }
          } else {
            this.billingInfo = null;
          }
        }
      } catch (error) {
        this.billingInfo = null;
      } finally {
        this.billingLoading = false;
      }
    },
    async applyPromoCode() {
      if (!this.promoCode.trim()) {
        this.$snackbar.add({
          type: 'error',
          text: this.$t('please_enter_promo_code')
        });
        return;
      }
      this.isApplyingPromo = true;
      try {
        const response = await promotionCodeService.validatePromotionCode({
          promotionCode: this.promoCode.trim().toUpperCase(),
          productUuid: this.product.uuid || this.product.id
        });
        if (response.data.responseType === 'success') {
          const pricingData = response.data.data.pricing;
          const promotionData = response.data.data.promotionCode;
          this.appliedPromotion = promotionData;
          this.discountAmount = pricingData.discountAmount;
          this.discountPercentage = pricingData.discountPercentage;
          this.$snackbar.add({
            type: 'success',
            text: this.$t('promo_code_applied_successfully') + ` %${pricingData.discountPercentage}`
          });
        } else {
          throw new Error(response.data.message || this.$t('promo_code_invalid'));
        }
      } catch (error) {
        this.appliedPromotion = null;
        this.discountAmount = 0;
        this.discountPercentage = 0;
        const errorMessage = error.response?.data?.message ||
            error.message || this.$t('promo_code_not_valid');
        this.$snackbar.add({
          type: 'error',
          text: errorMessage
        });
      } finally {
        this.isApplyingPromo = false;
      }
    },
    removePromoCode() {
      this.promoCode = '';
      this.appliedPromotion = null;
      this.discountAmount = 0;
      this.discountPercentage = 0;
      this.$snackbar.add({
        type: 'info',
        text: this.$t('promo_code_removed')
      });
    },
    formatPrice(amount) {
      if (amount === 0) return '0 ₺';
      return new Intl.NumberFormat('tr-TR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount) + ' TRY';
    },
    async getCards() {
      this.loading = true;
      try {
        const response = await pricingService.getPaymentMethods();
        this.paymentMethods = response?.data?.data?.data || [];
        if (this.paymentMethods.length > 0 && !this.selectedCardId) {
          const primaryCard = this.paymentMethods.find(card => card.isPrimary);
          this.selectedCardId = primaryCard ? primaryCard.cardId : this.paymentMethods[0]?.cardId;
        }
      } catch (error) {
        this.paymentMethods = [];
      } finally {
        this.loading = false;
      }
    },
    getCardBrandName(brandCode) {
      const brandNames = {
        'visa': 'Visa',
        'mastercard': 'Mastercard',
        'amex': 'American Express',
        'troy': 'Troy'
      };
      return brandNames[brandCode?.toLowerCase()] || brandCode || this.$t('credit_card');
    },
    selectPaymentCard(cardId) {
      this.selectedCardId = cardId;
      this.cardDropdownOpen = false;
    },
    closeCardDropdown() {
      this.cardDropdownOpen = false;
    },
    openAddCardModal() {
      this.isAddCardModalOpen = true;
    },
    closeAddCardModal() {
      this.isAddCardModalOpen = false;
    },
    async onCardSave(cardData) {
      if (cardData) {
        this.paymentMethods = [...this.paymentMethods, cardData];
      }
      setTimeout(async () => {
        await this.getCards();
      }, 500);
      this.closeAddCardModal();
    },
    openBillingModal() {
      this.isBillingModalOpen = true;
    },
    closeBillingModal() {
      this.isBillingModalOpen = false;
    },
    async onBillingSave() {
      this.closeBillingModal();
      await this.getBillingInfo();
    },
    openPreInformationModal() {
      this.isPreInformationModalOpen = true;
    },
    closePreInformationModal() {
      this.isPreInformationModalOpen = false;
    },
    acceptPreInformation() {
      this.agreements.preInformation = true;
      this.isPreInformationModalOpen = false;
    },
    openDistanceSalesModal() {
      this.isDistanceSalesModalOpen = true;
    },
    closeDistanceSalesModal() {
      this.isDistanceSalesModalOpen = false;
    },
    acceptDistanceSales() {
      this.agreements.distanceSales = true;
      this.isDistanceSalesModalOpen = false;
    },
    handlePaymentButtonClick() {
      if (!this.allAgreementsAccepted) {
        this.showValidationErrors = true;
        return;
      }
      if (!this.isProcessing) {
        this.processPayment();
      }
    }
  }
}
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-container {
  background: white;
  border-radius: 16px;
  width: 90vw;
  max-width: 1100px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  border-bottom: 1px solid #E5E7EB;
  flex-shrink: 0;
}

.back-btn,
.close-btn {
  background: #f5f5f5;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  color: #6B7280;
  transition: all 0.2s;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
}

.back-btn:hover,
.close-btn:hover {
  background: #F3F4F6;
  color: #374151;
}

.modal-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  font-weight: 600;
  color: #1F2937;
}

.payment-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: #EFF6FF;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.modal-content {
  display: contents;
}

.content-wrapper {
  display: flex;
  gap: 24px;
  width: 100%;
  align-items: flex-start;
  justify-content: center;
  padding: 24px;
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  max-height: calc(90vh - 70px);
}

.payment-section {
  flex: 1;
  min-width: 0;
  padding: 24px;
  border-radius: 12px;
  border: 1px solid #E5E7EB;
  background: #fff;
}

.summary-wrapper {
  flex: 0 0 340px;
  position: sticky;
  top: 0;
}

.summary-section {
  border-radius: 12px;
  border: 1px solid #EAECF0;
  background: #fff;
}

.section {
  margin-bottom: 24px;
}

.section-title {
  font-size: 15px;
  color: #1F2937;
  font-family: 'euclid_medium', sans-serif;
  margin: 0 0 12px 0;
}

.billing-loading,
.no-billing,
.cards-loading,
.no-cards {
  padding: 16px;
  text-align: center;
  color: #6B7280;
  font-size: 14px;
  background: #F9FAFB;
  border-radius: 8px;
}

.add-billing-link {
  color: #3B82F6;
  text-decoration: none;
  font-weight: 600;
  font-size: 14px;
  margin-top: 8px;
  display: inline-block;
  transition: color 0.2s;
}

.add-billing-link:hover {
  color: #2563EB;
  text-decoration: underline;
}

.billing-details {
  margin-top: 8px;
}

.form-row {
  margin-bottom: 8px;
}

.billing-name {
  font-size: 15px;
  font-weight: 600;
  color: #1F2937;
  margin: 0;
  text-align: left;
}

.address-info {
  color: #6B7280;
  font-size: 13px;
  line-height: 1.6;
}

.address-info p {
  margin: 2px 0;
  text-align: left;
}

.card-select-row {
  margin-bottom: 16px;
}

.card-select-wrapper {
  flex: 1;
  min-width: 0;
  position: relative;
}

.card-select-trigger {
  border-radius: 8px;
  border: 1px solid #F5F5F5;
  background: #F5F5F5;
  padding: 10px 0 10px 15px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  width: 100%;
}

.card-select-display {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  padding-right: 10px;
}

.card-select-icon {
  width: 35px;
  height: 24px;
  object-fit: contain;
  flex-shrink: 0;
}

.card-select-text {
  font-size: 14px;
  font-family: 'euclid_medium', sans-serif;
  color: #434343;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.card-select-expiry {
  color: #717171;
  font-size: 12px;
  margin-left: 8px;
}

.card-select-placeholder {
  font-size: 14px;
  font-family: 'euclid_regular', sans-serif;
  color: #717171;
  white-space: nowrap;
}

.card-select-arrow {
  width: 16px;
  height: 16px;
  margin-right: 10px;
  flex-shrink: 0;
  transition: transform 0.2s;
}

.card-select-arrow.rotated {
  transform: rotate(180deg);
}

.card-select-dropdown {
  position: absolute;
  top: calc(100% + 5px);
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid #EAECF0;
  border-radius: 8px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  z-index: 99999;
  max-height: 200px;
  overflow-y: auto;
}

.card-select-dropdown::-webkit-scrollbar {
  width: 15px;
}

.card-select-dropdown::-webkit-scrollbar-track {
  border-top-right-radius: 12px;
  border-bottom-right-radius: 12px;
  background-color: #fff;
}

.card-select-dropdown::-webkit-scrollbar-thumb {
  background-color: rgba(4, 12, 45, 0.2);
  border-radius: 10px;
  border: 5px transparent solid;
  background-clip: padding-box;
}

.card-select-dropdown::-webkit-scrollbar-thumb:hover {
  background-color: rgba(4, 12, 45, 0.4);
}

.card-select-option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  border-radius: 8px;
  cursor: pointer;
  font-family: 'euclid_regular', sans-serif;
  font-size: 12px;
}

.card-select-option:hover {
  background-color: #F5F7FA;
}

.card-select-option.selected {
  background-color: #F5F7FA;
}

.card-option-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
}

.card-option-brand {
  font-size: 13px;
  font-family: 'euclid_medium', sans-serif;
  color: #434343;
}

.card-option-number {
  font-size: 11px;
  font-family: 'euclid_regular', sans-serif;
  color: #717171;
}

.card-option-expiry {
  font-size: 11px;
  font-family: 'euclid_regular', sans-serif;
  color: #717171;
  flex-shrink: 0;
}

.no-card-info {
  border-radius: 8px;
  border: 1px solid #F5F5F5;
  background: #F5F5F5;
  padding: 10px 15px;
  display: flex;
  align-items: center;
}

.no-card-info span {
  color: #717171;
  font-size: 14px;
  font-family: 'euclid_regular', sans-serif;
}

.primary-button-wrapper {
  width: 100%;
  margin-top: 16px;
}

.add-new-btn {
  background: #1C3CD5;
  width: 100%;
  color: white;
  border: none;
  padding: 14px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.add-new-btn:hover {
  background: #1a35c0;
}

.payment-btn {
  width: 100%;
  background: #1F2937;
  color: white;
  border: none;
  padding: 14px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.payment-btn:hover {
  background: #111827;
}

.payment-btn:disabled {
  background: #9CA3AF;
  cursor: not-allowed;
  pointer-events: none;
}

.payment-btn-container {
  cursor: pointer;
}

.product-section {
  padding: 20px;
  border-bottom: 1px solid #E5E7EB;
}

.product-header {
  display: flex;
  gap: 14px;
  align-items: center;
}

.product-image {
  width: 72px;
  height: 72px;
  border-radius: 12px;
  object-fit: cover;
  flex-shrink: 0;
}

.product-info {
  flex: 1;
  min-width: 0;
}

.product-title {
  font-size: 15px;
  font-weight: 600;
  color: #1F2937;
  margin: 0;
  line-height: 1.4;
}

.order-summary {
  padding: 20px;
}

.price-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  gap: 12px;
}

.price-label {
  font-size: 14px;
  color: #6B7280;
}

.price-value {
  font-size: 14px;
  color: #1F2937;
  font-weight: 500;
}

.discount-row .price-value {
  color: #10B981;
}

.discount-code {
  font-size: 11px;
  color: #10B981;
  font-weight: 500;
}

.divider {
  height: 1px;
  background: #E5E7EB;
  margin: 16px 0;
}

.total-row {
  margin-top: 16px;
  margin-bottom: 0;
}

.total-row .price-label,
.total-row .price-value {
  font-size: 16px;
  font-weight: 600;
  color: #1F2937;
}

.promo-section {
  margin: 20px 0;
}

.promo-input-group {
  display: flex;
  gap: 8px;
}

.promo-input {
  flex: 1;
  min-width: 0;
  padding: 12px 14px;
  border: 1px solid #D1D5DB;
  border-radius: 8px;
  font-size: 14px;
  text-transform: uppercase;
}

.promo-input:focus {
  outline: none;
  border-color: #3B82F6;
}

.promo-btn {
  background: #3B82F6;
  color: white;
  border: none;
  padding: 12px 18px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.promo-btn:hover {
  background: #2563EB;
}

.promo-btn:disabled {
  background: #9CA3AF;
  cursor: not-allowed;
}

.applied-promo {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #F0FDF4;
  border: 1px solid #10B981;
  border-radius: 8px;
}

.applied-promo-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.applied-promo-code {
  font-size: 14px;
  font-weight: 600;
  color: #065F46;
}

.applied-promo-discount {
  font-size: 12px;
  color: #10B981;
}

.remove-promo-btn {
  background: none;
  border: none;
  color: #DC2626;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.remove-promo-btn:hover {
  background: #FEF2F2;
}

.security-badge {
  margin-top: 20px;
  padding: 14px;
  background: #EFF6FF;
  border-radius: 10px;
  font-size: 13px;
  color: #1E40AF;
  text-align: center;
}

.legal-agreements {
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.agreement-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.agreement-item.agreement-link-only {
  padding-left: 24px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  flex-shrink: 0;
}

.checkbox-label input[type="checkbox"] {
  width: 16px;
  height: 16px;
  margin: 0;
  cursor: pointer;
  accent-color: #3B82F6;
}

.checkmark {
  display: none;
}

.agreement-text {
  font-size: 13px;
  color: #4B5563;
  line-height: 1.4;
  text-align: left;
  flex: 1;
}

.agreement-link {
  color: #3B82F6;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s;
}

.agreement-link:hover {
  color: #2563EB;
  text-decoration: underline;
}

.agreement-item-wrapper {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.agreement-error {
  color: #DC2626;
  font-size: 12px;
  padding-left: 24px;
  text-align: left;
}

.desktop-only {
  display: block;
}

.mobile-payment-btn-wrapper {
  display: none;
}

@media screen and (max-width: 1100px) {
  .modal-container {
    width: 95vw;
  }

  .content-wrapper {
    gap: 20px;
  }

  .payment-section {
    padding: 20px;
  }

  .summary-wrapper {
    flex: 0 0 300px;
  }
}

@media screen and (max-width: 900px) {
  .modal-container {
    width: 95vw;
    max-width: 600px;
  }

  .content-wrapper {
    flex-direction: column;
    gap: 16px;
  }

  .payment-section {
    width: 100%;
    order: 2;
  }

  .summary-wrapper {
    flex: none;
    width: 100%;
    position: static;
    order: 1;
  }

  .modal-content {
    padding: 20px;
  }
}

@media screen and (max-width: 768px) {
  .modal-overlay {
    padding: 0;
    align-items: flex-end;
  }

  .modal-container {
    width: 100%;
    max-width: 100%;
    max-height: 95vh;
    border-radius: 20px 20px 0 0;
  }

  .modal-header {
    padding: 14px 16px;
  }

  .modal-title {
    font-size: 15px;
    gap: 8px;
  }

  .payment-icon {
    width: 32px;
    height: 32px;
  }

  .modal-content {
    padding: 16px;
    padding-bottom: 90px;
  }

  .content-wrapper {
    flex-direction: column;
    gap: 16px;
  }

  .payment-section {
    order: 2;
    padding: 16px;
  }

  .summary-wrapper {
    order: 1;
    position: static;
  }

  .desktop-only {
    display: none;
  }

  .mobile-payment-btn-wrapper {
    display: block;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 12px 16px;
    background: white;
    border-top: 1px solid #E5E7EB;
    box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.1);
    z-index: 10;
  }

  .section {
    margin-bottom: 20px;
  }

  .section-title {
    font-size: 14px;
    margin-bottom: 10px;
  }

  .product-section {
    padding: 14px;
  }

  .product-image {
    width: 60px;
    height: 60px;
    border-radius: 10px;
  }

  .product-title {
    font-size: 14px;
  }

  .add-new-btn {
    padding: 12px;
    font-size: 13px;
  }

  .payment-btn {
    padding: 14px;
    font-size: 14px;
  }

  .order-summary {
    padding: 14px;
  }

  .price-label,
  .price-value {
    font-size: 13px;
  }

  .total-row .price-label,
  .total-row .price-value {
    font-size: 15px;
  }

  .promo-section {
    margin: 14px 0;
  }

  .promo-input-group {
    flex-direction: column;
  }

  .promo-input {
    width: 100%;
    padding: 12px;
    font-size: 14px;
  }

  .promo-btn {
    width: 100%;
    padding: 12px;
    font-size: 14px;
  }

  .applied-promo {
    padding: 10px 12px;
  }

  .security-badge {
    margin-top: 14px;
    padding: 12px;
    font-size: 12px;
  }

  .billing-name {
    font-size: 14px;
  }

  .address-info {
    font-size: 12px;
  }
}

@media screen and (max-width: 480px) {
  .modal-container {
    max-height: 100vh;
    height: 100%;
    border-radius: 0;
  }

  .modal-header {
    padding: 12px;
  }

  .modal-title {
    font-size: 14px;
  }

  .payment-icon {
    width: 28px;
    height: 28px;
  }

  .back-btn,
  .close-btn {
    padding: 6px;
  }

  .modal-content {
    padding: 12px;
    padding-bottom: 80px;
  }

  .content-wrapper {
    gap: 12px;
  }

  .payment-section {
    padding: 12px;
  }

  .section {
    margin-bottom: 16px;
  }

  .section-title {
    font-size: 13px;
  }

  .product-section {
    padding: 12px;
  }

  .product-image {
    width: 50px;
    height: 50px;
  }

  .product-title {
    font-size: 13px;
  }

  .add-new-btn {
    padding: 10px;
    font-size: 12px;
  }

  .mobile-payment-btn-wrapper {
    padding: 10px 12px;
  }

  .payment-btn {
    padding: 12px;
    font-size: 13px;
  }

  .order-summary {
    padding: 12px;
  }

  .price-row {
    margin-bottom: 10px;
  }

  .price-label,
  .price-value {
    font-size: 12px;
  }

  .total-row .price-label,
  .total-row .price-value {
    font-size: 14px;
  }

  .divider {
    margin: 12px 0;
  }

  .promo-input {
    padding: 10px;
    font-size: 13px;
  }

  .promo-btn {
    padding: 10px;
    font-size: 13px;
  }

  .applied-promo {
    padding: 8px 10px;
  }

  .security-badge {
    padding: 10px;
    font-size: 11px;
  }

  .billing-name {
    font-size: 13px;
  }

  .address-info {
    font-size: 11px;
  }
}

/* Legal Modal Styles */
.legal-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
}

.legal-modal-container {
  background: white;
  border-radius: 16px;
  width: 90vw;
  max-width: 700px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
}

.legal-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #E5E7EB;
  flex-shrink: 0;
}

.legal-modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #1F2937;
}

.legal-modal-close {
  background: #f5f5f5;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  color: #6B7280;
  transition: all 0.2s;
  display: flex;
  justify-content: center;
  align-items: center;
}

.legal-modal-close:hover {
  background: #F3F4F6;
  color: #374151;
}

.legal-modal-content {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
}

.legal-text {
  font-size: 14px;
  line-height: 1.7;
  color: #4B5563;
}

.legal-text h4 {
  font-size: 15px;
  font-weight: 600;
  color: #1F2937;
  margin: 20px 0 12px 0;
  text-align: left;
}

.legal-text h4:first-child {
  margin-top: 0;
}

.legal-text p {
  margin: 8px 0;
  text-align: left;
}

.legal-text strong {
  color: #1F2937;
}

.legal-main-title {
  text-align: center;
  font-size: 18px;
  font-weight: 700;
  color: #1F2937;
  margin-bottom: 8px;
}

.legal-subtitle {
  text-align: center;
  font-size: 14px;
  color: #6B7280;
  margin-bottom: 24px;
}

.legal-text ul {
  margin: 12px 0;
  padding-left: 24px;
  text-align: left;
}

.legal-text li {
  margin: 6px 0;
}

.legal-modal-footer {
  padding: 16px 24px;
  border-top: 1px solid #E5E7EB;
  flex-shrink: 0;
}

@media screen and (max-width: 768px) {
  .legal-modal-overlay {
    padding: 0;
    align-items: flex-end;
  }

  .legal-modal-container {
    width: 100%;
    max-width: 100%;
    max-height: 90vh;
    border-radius: 20px 20px 0 0;
  }

  .legal-modal-header {
    padding: 16px 20px;
  }

  .legal-modal-header h3 {
    font-size: 16px;
  }

  .legal-modal-content {
    padding: 20px;
  }

  .legal-text {
    font-size: 13px;
  }

  .legal-text h4 {
    font-size: 14px;
  }

  .legal-modal-footer {
    padding: 14px 20px;
  }

  .legal-modal-accept-btn {
    padding: 12px;
    font-size: 14px;
  }
}
</style>
